import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { groupApi } from '../../services/admin/adminApi';
import { toast } from 'sonner';
import type { GroupCreateRequest, GroupUpdateRequest } from '../../types/admin';

/**
 * React Query hook for fetching all groups
 * @returns Query result with group list
 */
export function useGroups() {
  return useQuery({
    queryKey: ['admin', 'groups'],
    queryFn: () => groupApi.list(),
    select: (response) => response.data,
  });
}

/**
 * React Query hook for fetching single group with details
 * Includes related users and devices
 * @param id - Group (location) ID
 * @returns Query result with group details
 */
export function useGroup(id: number) {
  return useQuery({
    queryKey: ['admin', 'groups', id],
    queryFn: () => groupApi.get(id),
    select: (response) => response.data,
    enabled: !!id,
  });
}

/**
 * React Query mutation hook for creating new group
 * Invalidates group list on success
 * @returns Mutation result for group creation
 */
export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GroupCreateRequest) => groupApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
      toast.success('Group created successfully');
    },
    onError: (error: any) => {
      console.error('Failed to create group:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to create group');
    },
  });
}

/**
 * React Query mutation hook for updating group
 * Invalidates group list on success
 * @returns Mutation result for group update
 */
export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: GroupUpdateRequest }) =>
      groupApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
      toast.success('Group updated successfully');
    },
    onError: (error: any) => {
      console.error('Failed to update group:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to update group');
    },
  });
}

/**
 * React Query mutation hook for bulk deleting groups
 * Invalidates group list on success
 * @returns Mutation result for group deletion
 */
export function useDeleteGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (locationIds: number[]) => groupApi.delete(locationIds),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
      toast.success(`Successfully deleted ${variables.length} ${variables.length === 1 ? 'group' : 'groups'}`);
    },
    onError: (error: any) => {
      console.error('Failed to delete groups:', error);
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to delete groups');
    },
  });
}
